import { Component, effect, inject, NgZone, untracked } from '@angular/core';
import { filter, map, pairwise } from 'rxjs';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { PlatformService } from '@core/platform';

import { RESET_FOCUS_AREA_ID } from '../../constants';
import { FocusService } from '../../services/focus.service';

@Component({
  selector: 'wl-focus-reset-on-navigation',
  standalone: true,
  template: `<aside
    [id]="resetFocusAreaId"
    class="visually-hidden-focusable"
    tabindex="-1"
  ></aside>`,
  providers: [FocusService],
})
export class FocusResetOnNavigationComponent {
  resetFocusAreaId = RESET_FOCUS_AREA_ID;

  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);
  readonly #platformService = inject(PlatformService);
  readonly #focusService = inject(FocusService);
  readonly #zone = inject(NgZone);

  // see: https://angular.dev/best-practices/a11y#focus-management-after-navigation
  readonly navigationEnd = toSignal(
    this.#router.events.pipe(
      filter(() => this.#platformService.isClient()),
      filter((event) => event instanceof NavigationEnd),
      // For some unknown reason, when navigating with a #fragment on the same page
      // (e.g. what will happen when you "skip to main content"), a second navigation is dispatched to the
      // same page that will remove this fragment again. When this happens the focus should not be reset.
      // Also... we can safely remove any same-page-navigation focus-reset behaviour since this "effect"
      // is here to simulate the Non-SPA behaviour of navigating to another page.
      pairwise(),
      filter(
        ([previous, current]) =>
          previous.url.split('#')[0] !== current.url.split('#')[0]
      ),
      map(([_, current]) => current),
      // The first event appears on page-load via SSR, we don't care about this one.
      // All we care about are user-initiated internal navigations.
      filter((event) => event.id > 1)
    )
  );

  constructor() {
    effect(() => {
      // On any type of internal (client-side) navigation...
      const navigationEvent = this.navigationEnd();

      untracked(() => {
        // If there is no fragment (anchor) in the URL...
        if (navigationEvent && !this.#route.snapshot.fragment) {
          // Always reset the focus back to the beginning of the page
          // (as a macrotask at the end of any other queued tasks that may be dispatched on navigationEnd)
          this.#zone.runOutsideAngular(() =>
            setTimeout(() => this.#focusService.resetFocus())
          );
        }
      });
    });
  }
}
